/**
 * DIEUKYDIEU.TV - SOCIAL, LIKES, SHARE & MEDIA MODULE
 * LocalStorage anonymous likes, Web Share API, Desktop QR Code Share modal, Media library tabs.
 */

import { t } from './i18n.js';
import { state } from './state.js';
import { showToast } from './ui.js';
import { audioPlaylist } from './player.js';

/* ==========================================================================
   ANONYMOUS LIKE SYSTEM (LOCALSTORAGE)
   ========================================================================== */
export function getLikedList() {
    try {
        return JSON.parse(localStorage.getItem('dieukydieu_likes') || '[]');
    } catch (e) {
        return [];
    }
}

export function checkIsLiked(artId) {
    return getLikedList().includes(artId);
}

export function getArticleLikes(artId) {
    const base = 85 + (artId * 19) % 180;
    return checkIsLiked(artId) ? base + 1 : base;
}

export function toggleLikeArticle(artId, btnEl, e) {
    if (e) e.stopPropagation();
    let likes = getLikedList();
    const icon = btnEl.querySelector('.material-symbols-rounded');
    const counter = btnEl.querySelector('.like-counter');
    let currentNum = parseInt(counter.innerText) || 0;

    if (likes.includes(artId)) {
        likes = likes.filter(id => id !== artId);
        btnEl.classList.remove('liked');
        if (icon) icon.innerText = 'favorite_border';
        if (counter) counter.innerText = currentNum - 1;
        showToast(t('toast_unliked'));
    } else {
        likes.push(artId);
        btnEl.classList.add('liked');
        if (icon) icon.innerText = 'favorite';
        if (counter) counter.innerText = currentNum + 1;
        showToast(t('toast_liked'));
    }

    localStorage.setItem('dieukydieu_likes', JSON.stringify(likes));
    if (typeof window !== 'undefined' && typeof window.updateSidebarFavoritesBadge === 'function') {
        window.updateSidebarFavoritesBadge();
    }
}

/* ==========================================================================
   SHARE SYSTEM (MOBILE NATIVE & DESKTOP POPOVER WITH QR CODE)
   ========================================================================== */
export function openShareAction(title, url, e) {
    if (e) {
        if (typeof e.preventDefault === 'function') e.preventDefault();
        if (typeof e.stopPropagation === 'function') e.stopPropagation();
    }
    const shareTitle = title || state.currentShareData?.title || 'dieukydieu.tv';
    const shareUrl = url || state.currentShareData?.url || window.location.href;

    state.currentShareData = {
        title: shareTitle,
        url: shareUrl
    };

    // Prefer native system share menu (Android / iOS / supported browser)
    if (navigator.share) {
        navigator.share({
            title: shareTitle,
            text: `${shareTitle} - dieukydieu.tv`,
            url: shareUrl
        }).catch((err) => {
            // If user closed/cancelled the system share menu, do not open desktop popup
            if (err && (err.name === 'AbortError' || err.code === 20)) {
                return;
            }
            openShareModalDesktop(shareTitle, shareUrl);
        });
        return;
    }

    // Fallback: If on mobile but navigator.share is unavailable (e.g. unsecure HTTP LAN),
    // open the mobile-optimized bottom sheet share menu
    openShareModalDesktop(shareTitle, shareUrl);
}

export const SHARE_PLATFORMS = [
    { id: 'zalo', label: 'Zalo', badge: 'Zalo', brandClass: 'brand-zalo', action: "shareToPlatform('zalo')" },
    { id: 'facebook', label: 'Facebook', badge: 'FB', brandClass: 'brand-facebook', action: "shareToPlatform('facebook')" },
    { id: 'telegram', label: 'Telegram', badge: 'TG', brandClass: 'brand-telegram', action: "shareToPlatform('telegram')" },
    { id: 'copy', labelKey: 'share_copy_action', defaultLabel: 'Sao chép', icon: 'link', brandClass: 'brand-copy', action: 'copyCurrentShareLink()' }
];

export function renderShareChannels() {
    if (typeof document === 'undefined') return;
    const grid = document.querySelector('#share-modal .share-grid');
    if (!grid) return;
    grid.innerHTML = SHARE_PLATFORMS.map(ch => {
        const label = ch.labelKey ? (t(ch.labelKey) || ch.defaultLabel) : ch.label;
        const i18nAttr = ch.labelKey ? ` data-i18n="${ch.labelKey}"` : '';
        const iconContent = ch.icon
            ? `<span class="material-symbols-rounded">${ch.icon}</span>`
            : ch.badge;
        return `
            <button class="share-channel-btn" onclick="${ch.action}">
                <div class="share-channel-icon ${ch.brandClass}">${iconContent}</div>
                <span${i18nAttr}>${label}</span>
            </button>
        `;
    }).join('');
}

export function openShareModalDesktop(title, url) {
    renderShareChannels();
    const modal = document.getElementById('share-modal');
    const titleEl = document.getElementById('share-modal-title');
    const displayEl = document.getElementById('share-url-display');
    if (titleEl) titleEl.innerText = title;
    if (displayEl) displayEl.innerText = state.currentShareData.url;
    generateQRCode(state.currentShareData.url);
    if (modal) modal.classList.add('show');
}

export function closeShareModal(e) {
    const modal = document.getElementById('share-modal');
    if (modal) modal.classList.remove('show');
}

export function copyCurrentShareLink() {
    navigator.clipboard.writeText(state.currentShareData.url).then(() => {
        showToast(t('toast_link_copied'));
    });
}

export function shareToPlatform(platform) {
    const encodedUrl = encodeURIComponent(state.currentShareData.url);
    const encodedTitle = encodeURIComponent(state.currentShareData.title);
    let targetUrl = '';

    if (platform === 'zalo') {
        targetUrl = `https://zalo.me/share?url=${encodedUrl}`;
    } else if (platform === 'facebook') {
        targetUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
    } else if (platform === 'telegram') {
        targetUrl = `https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}`;
    }

    if (targetUrl) window.open(targetUrl, '_blank', 'noopener,noreferrer');
}

export function generateQRCode(text) {
    const container = document.getElementById('qr-code-container');
    if (!container) return;
    container.innerHTML = `
        <svg width="120" height="120" viewBox="0 0 100 100" fill="currentColor">
            <rect x="0" y="0" width="30" height="30" fill="none" stroke="currentColor" stroke-width="6" />
            <rect x="8" y="8" width="14" height="14" />
            <rect x="70" y="0" width="30" height="30" fill="none" stroke="currentColor" stroke-width="6" />
            <rect x="78" y="8" width="14" height="14" />
            <rect x="0" y="70" width="30" height="30" fill="none" stroke="currentColor" stroke-width="6" />
            <rect x="8" y="78" width="14" height="14" />
            <rect x="40" y="10" width="8" height="8" />
            <rect x="52" y="10" width="8" height="8" />
            <rect x="40" y="24" width="8" height="8" />
            <rect x="10" y="40" width="8" height="8" />
            <rect x="24" y="40" width="8" height="8" />
            <rect x="40" y="40" width="20" height="20" />
            <rect x="70" y="40" width="10" height="8" />
            <rect x="85" y="40" width="8" height="8" />
            <rect x="40" y="70" width="8" height="18" />
            <rect x="55" y="70" width="12" height="8" />
            <rect x="75" y="70" width="15" height="15" />
            <rect x="55" y="85" width="8" height="8" />
        </svg>
    `;
}

